import { AxiosError } from 'axios';
import {
  createContext,
  useEffect,
  useReducer,
  ReactNode,
  useCallback,
} from 'react';
import getAppVersion from 'api/appVersion';
import { Login, Logout, getAutorizationResponse } from 'api/authenticate';
import setAuthorizationToken from 'api/setAuthorizationToken';
import {
  // commonDataStore,
  filterDataStore,
  resetAllStores,
} from 'src/store';
import {
  type LoginResponse,
  type IUser,
  DefaultIUser,
  IdentityClaims,
} from 'types/loginResponse';
import { LoginType } from 'src/types/loginData';
// import { UserProfiles } from 'src/types/userProfiles';
import { SnackbarUtils } from 'src/utils/snackbarUtilsConfigurator';

interface IPayloadState {
  isAuthenticated: boolean;
  user: IUser;
  version: string;
  loginType: LoginType;
}

interface State extends Omit<IPayloadState, 'user'> {
  isInitialized: boolean;
  user: IUser | null;
}
interface AuthContextValue extends State {
  platform: 'JWT';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setLoginType: (loginType: LoginType) => void;
}
interface AuthProviderProps {
  children?: ReactNode;
}

interface InitializeAction {
  type: 'INITIALIZE';
  payload: IPayloadState;
}

interface LoginAction {
  type: 'LOGIN';
  payload: {
    user: IUser;
  };
}

interface LogoutAction {
  type: 'LOGOUT';
}
interface TypeLoginAction {
  type: 'SET_LOGIN_TYPE';
  payload: { loginType: LoginType };
}

type Action = InitializeAction | LoginAction | LogoutAction | TypeLoginAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  version: '',
  loginType: LoginType.NORMAL,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: Action): State => {
    const { isAuthenticated, user, version } = (action as InitializeAction)
      .payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      version,
    };
  },
  LOGIN: (state: State, action: Action): State => {
    const { user } = (action as LoginAction).payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
    loginType: LoginType.NORMAL,
  }),
  SET_LOGIN_TYPE: (state: State, action: Action): State => {
    const { loginType } = (action as TypeLoginAction).payload;
    return {
      ...state,
      loginType,
    };
  },
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type](state, action);

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {
    // lala
  },
  setLoginType: () => {
    // lala
  },
});

export const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  // const { setBaseData } = commonDataStore((state) => state);
  const resetfilter = filterDataStore((state) => state.reset);

  const initialize = useCallback(
    async () => {
      const payload: IPayloadState = {
        isAuthenticated: false,
        user: DefaultIUser,
        version: '',
        loginType: state.loginType,
      };

      try {
        const authorizationToken =
          localStorage.getItem('authorizationToken') ?? '';
        const identityData = localStorage.getItem('IdentityData') ?? '';
        if (authorizationToken) setAuthorizationToken(authorizationToken);

        try {
          const responseVersion = await getAppVersion();
          payload.version = responseVersion;
          localStorage.setItem('version', responseVersion);
        } catch (err) {
          SnackbarUtils.error('Ocurrió un error inicializando la aplicación');
          // eslint-disable-next-line no-console
          console.error(err);
        }

        if (authorizationToken !== '') {
          const { claimsResponse, succeeded } =
            getAutorizationResponse(authorizationToken);
          const { userName, name, email, rut, rol } = JSON.parse(
            identityData
          ) as IdentityClaims;

          if (succeeded) {
            payload.user = {
              userName,
              name,
              email,
              claims: claimsResponse,
              rut,
              rol: Number(rol),
            } as unknown as IUser;
            payload.isAuthenticated = true;
            localStorage.setItem('authorizationToken', authorizationToken);
            setAuthorizationToken(authorizationToken);
            // setBaseData();
            resetfilter();
          }
        }

        dispatch({
          type: 'INITIALIZE',
          payload: payload,
        });
      } catch (err) {
        dispatch({
          type: 'INITIALIZE',
          payload: payload,
        });
      }
    },
    // [setBaseData, resetfilter]
    [resetfilter]
  );

  useEffect(() => {
    initialize().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Error during authentication initialization:', err);
    });
  }, []);

  function setSession(loginResponse: LoginResponse | null): void {
    if (loginResponse?.succeeded) {
      const { authorization } = loginResponse;
      localStorage.setItem('authorizationToken', authorization);
      setAuthorizationToken(authorization);
      // if (loginResponse.user.profile !== UserProfiles.Conductor) setBaseData();
    } else {
      localStorage.removeItem('authorizationToken');
      localStorage.removeItem('IdentityData');
      setAuthorizationToken(null);
    }
  }

  const login = async (username: string, password: string): Promise<void> => {
    const response = await Login({ username, password });
    if (typeof response === 'string') throw new Error(response);
    setSession(response);
    // if (!response.succeeded) {
    //   throw new Error(response.message);
    // }
    dispatch({
      type: 'LOGIN',
      payload: {
        user: response.user,
      },
    });
  };

  const logout = () => {
    const version = localStorage.getItem('version') ?? '';
    const authorization = localStorage.getItem('authorizationToken') ?? '';
    setSession(null);
    dispatch({ type: 'LOGOUT' });
    resetAllStores();
    Logout({ authorization, version })
      // eslint-disable-next-line no-console
      .then(() => console.log('logged out'))
      .catch((err: AxiosError) => {
        // eslint-disable-next-line no-console
        console.error(err.message);
      });
  };

  const setLoginType = (loginType: LoginType) => {
    dispatch({
      type: 'SET_LOGIN_TYPE',
      payload: {
        loginType,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        setLoginType,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
